import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {Kyc} from '~local/app/types/Kyc';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {DataSourceResponse, DataSourceService} from './DataSourceService';
import {SaveFormConfig} from '~shared/ng/types/ng-form-types';


@Injectable()
export class KycService extends DataSourceService<Kyc> {
    constructor(private readonly http: HttpClient, private readonly domSanitizer: DomSanitizer) {
        super();
    }

    public loadData(
        filter: string = '',
        orderBy: string = '',
        orderDirection: string = 'ASC',
        pageNumber: number = 0,
        pageSize: number = 10,
        globalFilter: string = null
    ): Observable<DataSourceResponse<Kyc>> {
        return this.http.get<DataSourceResponse<Kyc>>('/admin/kyc/list/' + globalFilter, {
            params: new HttpParams()
                .set('filter', filter)
                .set('orderBy', orderBy)
                .set('sort', orderDirection.toUpperCase())
                .set('skip', pageNumber.toString())
                .set('take', pageSize.toString())
        });
    }

    public getKyc(id: number): Observable<Kyc> {
        return this.http.get<Kyc>('/admin/kyc/' + id);
    }

    public async getImageOld(key: string): Promise<SafeUrl> {
        const res = await this.http.get<{ data: number[]; type: 'Buffer' }>('/admin/kyc/image/' + key).toPromise();
        let binary = '';
        const bytes = new Uint8Array(res.data);
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        const base64String = btoa(binary);
        return this.domSanitizer.bypassSecurityTrustUrl('data:image/jpg;base64, ' + base64String);
    }

    public getImage(key: string): Observable<SafeUrl> {
        return this.http.get<{ data: number[]; type: 'Buffer' }>('/admin/kyc/image/' + key).pipe(
            map((buffer) => {
                let binary = '';
                const bytes = new Uint8Array(buffer.data);
                for (let i = 0; i < bytes.byteLength; i++) {
                    binary += String.fromCharCode(bytes[i]);
                }
                const base64String = btoa(binary);
                return this.domSanitizer.bypassSecurityTrustUrl('data:image/jpg;base64, ' + base64String);
            })
        );
    }

    public getIdentity(id: number): Observable<any> {
        return this.http.get<any>(`/admin/kyc/identity/${id}`);
    }

    public getInterview(id: number): Observable<any> {
        if (id && id !== null) {
            return this.http.get<any>(`/admin/kyc/interview/${id}`);
        }
        return this.http.get<any>(`/admin/kyc/interview`);
    }

    public reject(formData: {
        action: string;
        kycTierId: number;
        final: boolean;
        rejectionReason?: string
    }): Observable<any> {
        return this.http.post<any>('/admin/kyc/action', formData);
    }

    public accept(kycTierId: number, formConfig: SaveFormConfig = null): Observable<any> {
        return this.http.post<any>('/admin/kyc/action', {action: 'accept', kycTierId, formConfig});
    }

    public rollback(kycTierId: number): Observable<any> {
        return this.http.put<{ status: "success" }>('/admin/kyc/rollback', {kycTierId});
    }

    public saveInterview(formData: any): Observable<any> {
        return this.http.post<any>('/admin/kyc/interview/save', formData);
    }

    public getCalendarInterviews(): Observable<any> {
        return this.http.get<any>(`/admin/kyc/calendar/interviews`);
    }

    public getKycTierLimitList(profile: string): Observable<any> {
        return this.http.get<any>(`/admin/kyc/limit/list/${profile}`);
    }

    public updateKycTierLimit(formData: any): Observable<any> {
        return this.http.put<any>('/admin/kyc/limit/update', formData);
    }

    public getKycTierLimit(tier: number, profile: string): Observable<any> {
        return this.http.get<any>(`/admin/kyc/limit/${tier}/${profile}`);
    }

    public getKycGroupTiers(tier: number, profile: string): Observable<any> {
        return this.http.get<any>(`/admin/kyc/group/${tier}/${profile}`);
    }

    public changeUserKycTier(formData: any): Observable<any> {
        return this.http.put<any>('/admin/kyc/tier/change', formData);
    }

    public confirmUserKycTierChange(transactionId: number): Observable<any> {
        return this.http.put<any>('/admin/kyc/tier/confirm', {transactionId: transactionId});
    }
}
